import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Button,
  Divider
} from '@mui/material';
import { 
  ArrowBack, 
  Notifications,
  CalendarToday,
  Visibility
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';

interface Notice {
  id: string;
  title: string;
  content: string;
  date: string;
  type: '공지' | '이벤트' | '시스템';
  isImportant: boolean;
  views: number;
}

const mockNotices: Notice[] = [
  {
    id: '1',
    title: '[연휴공지] 오픈리뷰 캠페인 승인 및 고객센터, 카카오채널 상담 일정',
    content: '설 연휴 기간 동안 캠페인 승인 및 고객센터 상담 일정이 변경됩니다...',
    date: '2024-01-15',
    type: '공지',
    isImportant: true,
    views: 1250
  },
  {
    id: '2',
    title: '2024년 1월 시스템 점검 안내',
    content: '2024년 1월 20일 새벽 2시부터 4시까지 시스템 점검이 진행됩니다...',
    date: '2024-01-10',
    type: '시스템',
    isImportant: false,
    views: 890
  },
  {
    id: '3',
    title: '새로운 캠페인 등록 기능 업데이트',
    content: '캠페인 등록 시 더욱 편리한 기능들이 추가되었습니다...',
    date: '2024-01-05',
    type: '공지',
    isImportant: false,
    views: 567
  },
  {
    id: '4',
    title: '[이벤트] 신규 가입자 환영 이벤트',
    content: '신규 가입자를 위한 특별한 이벤트가 진행됩니다...',
    date: '2024-01-01',
    type: '이벤트',
    isImportant: false,
    views: 2340
  }
];

const getTypeColor = (type: string) => {
  switch (type) {
    case '공지': return 'primary';
    case '이벤트': return 'success';
    case '시스템': return 'warning';
    default: return 'default';
  }
};

const NoticePage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedNotice, setSelectedNotice] = useState<Notice | null>(null);

  const handleBack = () => {
    if (selectedNotice) {
      setSelectedNotice(null);
    } else {
      navigate('/mypage/reviewer');
    }
  };

  const handleNoticeClick = (notice: Notice) => {
    setSelectedNotice(notice);
  };

  if (selectedNotice) {
    return (
      <>
        <Header showIcons={true} />
        <Container maxWidth="md" sx={{ pb: 8, pt: 0 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, mt: 2 }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBack}
              sx={{ mr: 2 }}
            >
              뒤로
            </Button>
            <Typography variant="h5" component="h1">
              공지사항
            </Typography>
          </Box>

          {/* Notice Detail */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
                  {selectedNotice.title}
                </Typography>
                <Chip
                  label={selectedNotice.type}
                  color={getTypeColor(selectedNotice.type) as any}
                  size="small"
                />
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarToday fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {selectedNotice.date}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Visibility fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    조회수 {selectedNotice.views}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                {selectedNotice.content}
              </Typography>

              {selectedNotice.isImportant && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                  <Typography variant="body2" color="warning.dark">
                    ⚠️ 이 공지사항은 중요 공지사항입니다. 반드시 확인해주세요.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Container>
      </>
    );
  }

  return (
    <>
      <Header showIcons={true} />
      <Container maxWidth="md" sx={{ pb: 8, pt: 0 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, mt: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            뒤로
          </Button>
          <Typography variant="h5" component="h1">
            공지사항
          </Typography>
        </Box>

        {/* Notice List */}
        <Card>
          <List>
            {mockNotices.map((notice, index) => (
              <React.Fragment key={notice.id}>
                <ListItem 
                  button 
                  onClick={() => handleNoticeClick(notice)}
                  sx={{ 
                    flexDirection: 'column', 
                    alignItems: 'flex-start',
                    py: 2
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <ListItemIcon sx={{ minWidth: 'auto' }}>
                        <Notifications color="primary" />
                      </ListItemIcon>
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          fontWeight: notice.isImportant ? 'bold' : 'normal',
                          color: notice.isImportant ? 'primary.main' : 'inherit'
                        }}
                      >
                        {notice.title}
                      </Typography>
                    </Box>
                    <Chip
                      label={notice.type}
                      color={getTypeColor(notice.type) as any}
                      size="small"
                    />
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarToday fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {notice.date}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Visibility fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {notice.views}
                      </Typography>
                    </Box>
                  </Box>
                </ListItem>
                {index < mockNotices.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Card>
      </Container>
    </>
  );
};

export default NoticePage; 